import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import VerifyDataAccess from './components/VerifyDataAccess';
import reportWebVitals from './reportWebVitals';
import { initializeMSAL, AuthProvider } from './auth/AuthContext';
import { dlog } from './utils/debug';

// Where the backend bounces a viewer whose access to a dashboard's SharePoint
// source hasn't been confirmed (see routers/website_routes.py's
// _redirect_to_access_check). It renders INSTEAD of <App/>, not inside it:
// all this page does is hand MSAL's token to one endpoint and redirect, and
// mounting App would fire its whole startup -- /models, /projects,
// /focus-modes, a conversation load -- to serve a detour the viewer should
// barely see. AuthProvider stays, since the page needs the access_token it
// puts in localStorage.
const ACCESS_CHECK_PATH = '/verify-data-access';

// Loading component
const Loading = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #0078d4',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Loading...</h2>
      <p style={{ color: '#666', margin: 0 }}>Initializing authentication...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Error component
const ErrorComponent = ({ error }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '1rem'
  }}>
    <div style={{
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      textAlign: 'center',
      maxWidth: '500px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        backgroundColor: '#d13438',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1rem',
        color: 'white',
        fontSize: '20px'
      }}>
        ✗
      </div>
      <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Initialization Failed</h2>
      <p style={{ color: '#666', marginBottom: '1rem' }}>
        Failed to initialize authentication system.
      </p>
      <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Error: {error}
      </p>
      <button 
        onClick={() => window.location.reload()}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#0078d4',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Retry
      </button>
    </div>
  </div>
);

// Initialize and render the app
const initializeAndRenderApp = async () => {
  const root = ReactDOM.createRoot(document.getElementById('root'));

  try {
    // Show loading screen
    root.render(<Loading />);

    // Initialize MSAL
    dlog('Initializing MSAL...');
    const msalInstance = await initializeMSAL();
    dlog('MSAL initialized successfully');

    // Handle redirect promise BEFORE rendering the app
    // This is critical - MSAL needs to process any redirect response
    // before React renders, otherwise auth state won't be synchronized
    dlog('Handling redirect promise...');
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      dlog('Redirect response received:', response.account?.username);
    } else {
      dlog('No redirect response (normal page load)');
    }

    // Render the app with AuthProvider
    const isAccessCheck = window.location.pathname === ACCESS_CHECK_PATH;
    root.render(
      <React.StrictMode>
        <AuthProvider msalInstance={msalInstance}>
          {isAccessCheck ? <VerifyDataAccess /> : <App />}
        </AuthProvider>
      </React.StrictMode>
    );

  } catch (error) {
    console.error('Failed to initialize app:', error);
    root.render(<ErrorComponent error={error.message} />);
  }
};

// Start the initialization
initializeAndRenderApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();